/**
 * useMyDeals Hook
 *
 * Fetches customer's deals with pagination support.
 * Uses the getMyDeals query from the CRM service.
 *
 * Requirements: Task 6 - My Deals Page
 */

import { useMemo, useCallback } from 'react';
import { useGetMyDealsQuery, type GetMyDealsQuery, type PaginationInput } from '@bloomcode/graphql';

// Type aliases for query results
type DealItem = GetMyDealsQuery['getMyDeals']['data'][number];
type PaginationInfo = GetMyDealsQuery['getMyDeals']['pagination'];

interface UseMyDealsOptions {
  page?: number;
  limit?: number;
  skip?: boolean;
}

interface UseMyDealsResult {
  deals: DealItem[];
  pagination: PaginationInfo | null;
  totalCount: number;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook to fetch the current customer's deals with pagination
 *
 * @param options - Configuration options
 * @returns Customer deals data with loading and error states
 */
export function useMyDeals(options: UseMyDealsOptions = {}): UseMyDealsResult {
  const { page = 1, limit = 10, skip = false } = options;

  const pagination: PaginationInput = { page, limit };

  const {
    data,
    loading,
    error,
    refetch: refetchQuery,
  } = useGetMyDealsQuery({
    variables: { pagination },
    skip,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const deals: DealItem[] = useMemo(() => data?.getMyDeals?.data ?? [], [data]);

  const paginationInfo: PaginationInfo | null = useMemo(
    () => data?.getMyDeals?.pagination ?? null,
    [data]
  );

  const totalCount = paginationInfo?.total ?? 0;

  const refetch = useCallback(() => {
    refetchQuery();
  }, [refetchQuery]);

  return {
    deals,
    pagination: paginationInfo,
    totalCount,
    isLoading: loading,
    error: error ?? null,
    refetch,
  };
}

export type { DealItem, PaginationInfo };
export default useMyDeals;
